import React, { forwardRef, useState, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../assets/styles/datepicker-custom.css';
import { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';

registerLocale('pt-BR', ptBR);

export const dateHelpers = {
    stringToDate: (dateString) => {
        if (!dateString || !dateString.includes('/')) {
            return null;
        }
        const [day, month, year] = dateString.split('/');
        if (!day || !month || !year || day.length < 1 || month.length < 1 || year.length !== 4) {
            return null;
        }
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    },

    dateToString: (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    },

    createOnChangeHandler: (onChangeCallback) => {
        return (date) => {
            if (!date) {
                onChangeCallback('');
                return;
            }
            const formattedDate = dateHelpers.dateToString(date);
            onChangeCallback(formattedDate);
        };
    }
};

const applyDateMask = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 8) {
        cleaned = cleaned.substring(0, 8);
    }

    let formatted = '';
    if (cleaned.length > 0) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) {
            formatted += '/' + cleaned.substring(2, 4);
        }
        if (cleaned.length > 4) {
            formatted += '/' + cleaned.substring(4, 8);
        }
    }

    return formatted;
};

const applyDateTimeMask = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 12) {
        cleaned = cleaned.substring(0, 12);
    }

    let formatted = '';
    if (cleaned.length > 0) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) {
            formatted += '/' + cleaned.substring(2, 4);
        }
        if (cleaned.length > 4) {
            formatted += '/' + cleaned.substring(4, 8);
        }
        if (cleaned.length > 8) {
            formatted += ' ' + cleaned.substring(8, 10);
        }
        if (cleaned.length > 10) {
            formatted += ':' + cleaned.substring(10, 12);
        }
    }

    return formatted;
};

const applyTimeMask = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 4) {
        cleaned = cleaned.substring(0, 4);
    }

    let formatted = '';
    if (cleaned.length > 0) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) {
            formatted += ':' + cleaned.substring(2, 4);
        }
    }

    return formatted;
};

const parseInputToDate = (inputValue, hasTime = false, timeOnly = false) => {
    if (!inputValue) return null;

    if (timeOnly) {
        const parts = inputValue.split(':');
        if (parts.length === 2) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                const date = new Date();
                date.setHours(hours, minutes, 0, 0);
                return date;
            }
        }
        return null;
    }

    const dateTimeParts = inputValue.split(' ');
    const datePart = dateTimeParts[0];
    const timePart = dateTimeParts[1];

    const parts = datePart.split('/');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (day < 1 || day > 31) return null;
    if (month < 1 || month > 12) return null;
    if (year < 1900 || year > 2100) return null;
    if (parts[2].length !== 4) return null;

    let hours = 0;
    let minutes = 0;

    if (hasTime && timePart) {
        const timePartsSplit = timePart.split(':');
        if (timePartsSplit.length === 2) {
            hours = parseInt(timePartsSplit[0], 10);
            minutes = parseInt(timePartsSplit[1], 10);
            if (isNaN(hours) || isNaN(minutes)) return null;
            if (hours < 0 || hours > 23) return null;
            if (minutes < 0 || minutes > 59) return null;
        }
    }

    const date = new Date(year, month - 1, day, hours, minutes);

    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
        return null;
    }

    return date;
};

const CustomInput = forwardRef(({
    value,
    placeholder,
    className,
    maxLength = 10,
    onInputChange,
    hasTime,
    timeOnly,
    onCalendarClick,
    isClearable,
    onClear
}, ref) => {
    const [inputValue, setInputValue] = useState(value || '');

    React.useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const handleChange = (e) => {
        const rawValue = e.target.value;
        let masked;

        if (timeOnly) {
            masked = applyTimeMask(rawValue);
        } else if (hasTime) {
            masked = applyDateTimeMask(rawValue);
        } else {
            masked = applyDateMask(rawValue);
        }

        setInputValue(masked);

        if (onInputChange) {
            onInputChange(masked);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setInputValue('');
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className="input-group">
            <input
                className={className}
                placeholder={placeholder}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                ref={ref}
                maxLength={maxLength}
                autoComplete="off"
            />
            {isClearable && inputValue && (
                <button
                    type="button"
                    className="btn btn-clear"
                    onClick={handleClear}
                    title="Limpar"
                >
                    <FaTimes />
                </button>
            )}
            <button
                type="button"
                className="btn btn-calendar"
                onClick={onCalendarClick}
                title="Abrir calendário"
            >
                <FaCalendarAlt />
            </button>
        </div>
    );
});

const DatePicker = ({
    selected,
    onChange,
    placeholderText,
    dateFormat,
    className = "form-control",
    isClearable = true,
    showYearDropdown = true,
    showMonthDropdown = true,
    dropdownMode = "select",
    locale = "pt-BR",
    showTimeSelect = false,
    showTimeOnly = false,
    timeIntervals = 15,
    timeCaption = "Hora",
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const datePickerRef = useRef(null);

    const getDateConfig = () => {
        if (showTimeOnly) {
            return {
                dateFormat: dateFormat || "HH:mm",
                placeholderText: placeholderText || "hh:mm",
                maxLength: 5,
                showYearDropdown: false,
                showMonthDropdown: false
            };
        } else if (showTimeSelect) {
            return {
                dateFormat: dateFormat || "dd/MM/yyyy HH:mm",
                placeholderText: placeholderText || "dd/mm/aaaa hh:mm",
                maxLength: 16,
                showYearDropdown: true,
                showMonthDropdown: true
            };
        } else {
            return {
                dateFormat: dateFormat || "dd/MM/yyyy",
                placeholderText: placeholderText || "dd/mm/yyyy",
                maxLength: 10,
                showYearDropdown: true,
                showMonthDropdown: true
            };
        }
    };

    const config = getDateConfig();

    const handleInputChange = (maskedValue) => {
        const parsedDate = parseInputToDate(maskedValue, showTimeSelect, showTimeOnly);
        if (parsedDate) {
            onChange(parsedDate);
        } else if (maskedValue === '') {
            onChange(null);
        }
    };

    const handleCalendarClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleCalendarClose = () => {
        setIsOpen(false);
    };

    const handleDateChange = (date) => {
        onChange(date);
        if (!showTimeSelect) {
            setIsOpen(false);
        }
    };

    const handleClear = () => {
        onChange(null);
    };

    return (
        <ReactDatePicker
            ref={datePickerRef}
            selected={selected}
            onChange={handleDateChange}
            dateFormat={config.dateFormat}
            locale={locale}
            placeholderText={config.placeholderText}
            isClearable={false}
            showYearDropdown={config.showYearDropdown}
            showMonthDropdown={config.showMonthDropdown}
            dropdownMode={dropdownMode}
            showTimeSelect={showTimeSelect}
            showTimeOnly={showTimeOnly}
            timeIntervals={timeIntervals}
            timeCaption={timeCaption}
            open={isOpen}
            onClickOutside={handleCalendarClose}
            customInput={
                <CustomInput
                    className={className}
                    placeholder={config.placeholderText}
                    maxLength={config.maxLength}
                    onInputChange={handleInputChange}
                    hasTime={showTimeSelect}
                    timeOnly={showTimeOnly}
                    onCalendarClick={handleCalendarClick}
                    isClearable={isClearable}
                    onClear={handleClear}
                />
            }
            {...props}
        />
    );
};

export default DatePicker;